// pages/Dashboard/SalesDashboard.jsx

import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  TrendingUp,
  Calendar,
  Download,
  RefreshCw,
  Award,
  Package,
  DollarSign,
  Users,
  Phone,
  CheckCircle,
  XCircle,
  BarChart3,
  ChevronRight,
  ChevronDown,
  X,
  Clock,
  Target,
  Percent,
} from "lucide-react";
import apiService from "../../services/api";
import { useTheme } from "../../contexts/ThemeContext";

// ============================================
// UTILITY FUNCTIONS
// ============================================

const formatCurrency = (value) => {
  if (value === null || value === undefined) return "£0.00";
  return new Intl.NumberFormat("en-GB", {
    style: "currency",
    currency: "GBP",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);
};

const formatNumber = (value) => {
  if (value === null || value === undefined) return "0";
  return new Intl.NumberFormat("en-GB").format(value);
};

const formatDate = (dateString) => {
  if (!dateString) return "-";
  return new Date(dateString).toLocaleDateString("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
};

const formatPercent = (value) => {
  if (value === null || value === undefined) return "0%";
  return `${parseFloat(value).toFixed(1)}%`;
};

// ============================================
// HELPER: Determine group field and value from report + row data
// ============================================

const getGroupInfo = (report, memberData) => {
  // Use the report's config.groupBy (from the template) to determine the correct field
  const groupBy = report.config?.groupBy || report.results?.config?.groupBy;

  switch (groupBy) {
    case "assignedTo":
      return {
        groupField: "assignedUser.name",
        groupValue:
          memberData.assignedUser?.name ||
          memberData["assignedUser.name"] ||
          "Unknown",
      };
    case "coachId":
      return {
        groupField: "coach.name",
        groupValue:
          memberData.coach?.name || memberData["coach.name"] || "Unknown",
      };
    case "telesalesPerson":
      return {
        groupField: "telesalesPerson",
        groupValue: memberData.telesalesPerson || "Unknown",
      };
    case "bookedDate":
      return {
        groupField: "telesalesPerson",
        groupValue: memberData.telesalesPerson || "Unknown",
      };
    default:
      // Fallback: infer from which fields are present in the data
      // Check assignedUser first if telesalesPerson is absent
      if (
        (memberData.assignedUser?.name || memberData["assignedUser.name"]) &&
        !memberData.telesalesPerson
      ) {
        return {
          groupField: "assignedUser.name",
          groupValue:
            memberData.assignedUser?.name ||
            memberData["assignedUser.name"] ||
            "Unknown",
        };
      }
      if (
        (memberData.coach?.name || memberData["coach.name"]) &&
        !memberData.telesalesPerson
      ) {
        return {
          groupField: "coach.name",
          groupValue:
            memberData.coach?.name || memberData["coach.name"] || "Unknown",
        };
      }
      // Default to telesalesPerson
      return {
        groupField: "telesalesPerson",
        groupValue: memberData.telesalesPerson || "Unknown",
      };
  }
};

// ============================================
// MAIN DASHBOARD COMPONENT
// ============================================

const SalesDashboard = () => {
  const { isDarkMode } = useTheme();
  const [currentMonthPeriod, setCurrentMonthPeriod] = useState(null);
  const [telemarketingReports, setTelemarketingReports] = useState({});
  const [selectedPeriod, setSelectedPeriod] = useState(0); // 0 = current
  const [availablePeriods, setAvailablePeriods] = useState([]);
  const [salesReports, setSalesReports] = useState({});
  console.log("aa", salesReports);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [selectedReport, setSelectedReport] = useState(null);
  const [activeTab, setActiveTab] = useState("telemarketing");
  const [lastRefresh, setLastRefresh] = useState(null);
  const [error, setError] = useState(null);

  const refreshIntervalRef = useRef(null);

  // Report developer names
  const TELEMARKETING_REPORTS = [
    "total_booked_appts_tm_current_month",
    "qual_sat_performance_tm_current_month",
    "base_qual_sat_performance_tm_current_month",
    "new_business_qual_sat_performance_tm_current_month",
    "deals_sold_units_tm_current_month",
    "deals_installed_units_tm_current_month",
    "day_by_day_booking_current_month",
    "coach_performance_current_month",
    "cancelled_appointments_tm_current_month",
  ];

  const SALES_REPORTS = [
    "sold_deals_sales_rep_current_month",
    "installed_sales_rep_current_month",
    "qual_sat_percentage_sales_rep_current_month",
    "base_qual_sat_performance_sales_rep_current_month",
    "new_business_qual_sat_performance_sales_rep_current_month",
    "appointments_booked_sales_rep_current_month",
    "sale_originated_breakdown_current_month",
  ];

  // Load dashboard data
  const loadDashboardData = useCallback(
    async (showRefreshing = true, forceRefresh = false) => {
      try {
        if (showRefreshing) setRefreshing(true);
        setError(null);

        // Get current month period
        const periodResponse = await apiService.getCurrentMonthPeriod();
        setCurrentMonthPeriod(periodResponse.data);

        // Get all reports
        const reportsResponse = await apiService.getReports({
          isPublic: true,
          reportType: "opportunities",
          limit: 100,
        });

        const telemarketingData = {};
        const salesData = {};

        // Run each report with selected period
        for (const report of reportsResponse.data) {
          try {
            if (TELEMARKETING_REPORTS.includes(report.developerName)) {
              const result = await apiService.runReport(
                report.id,
                {},
                forceRefresh,
                selectedPeriod, // NEW: Pass selected period
              );
              telemarketingData[report.developerName] = {
                ...report,
                results: result,
              };
            } else if (SALES_REPORTS.includes(report.developerName)) {
              const result = await apiService.runReport(
                report.id,
                {},
                forceRefresh,
                selectedPeriod, // NEW: Pass selected period
              );
              salesData[report.developerName] = {
                ...report,
                results: result,
              };
            }
          } catch (err) {
            console.error(`Failed to run report ${report.name}:`, err);
          }
        }

        setTelemarketingReports(telemarketingData);
        setSalesReports(salesData);
        setLastRefresh(new Date());
      } catch (err) {
        console.error("Failed to load dashboard data:", err);
        setError("Failed to load dashboard data. Please try again.");
      } finally {
        setLoading(false);
        setRefreshing(false);
      }
    },
    [selectedPeriod], // NEW: Add dependency
  );

  // Reload when period changes
  useEffect(() => {
    if (availablePeriods.length > 0) {
      loadDashboardData(true, false);
    }
  }, [selectedPeriod, loadDashboardData, availablePeriods.length]);
  useEffect(() => {
    const loadPeriods = async () => {
      try {
        const response = await apiService.getAvailablePeriods();
        setAvailablePeriods(response.data);
      } catch (err) {
        console.error("Failed to load periods:", err);
      }
    };
    loadPeriods();
  }, []);
  // Initial load
  useEffect(() => {
    loadDashboardData(false, false); // Don't force refresh on initial load
  }, [loadDashboardData]);

  // Set up hourly refresh
  useEffect(() => {
    refreshIntervalRef.current = setInterval(() => {
      loadDashboardData(true, false); // Use cache for auto-refresh
    }, 3600000);

    return () => {
      if (refreshIntervalRef.current) {
        clearInterval(refreshIntervalRef.current);
      }
    };
  }, [loadDashboardData]);

  // Export report handler
  const handleExportReport = async (
    reportKey,
    reportType = "telemarketing",
    format = "csv", // 'csv' or 'pdf'
  ) => {
    try {
      const reports =
        reportType === "telemarketing" ? telemarketingReports : salesReports;
      const report = reports[reportKey];
      if (!report) return;

      const blob = await apiService.exportReport(report.id, format);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${report.developerName}_${new Date().toISOString().split("T")[0]}.${format}`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err) {
      console.error("Export failed:", err);
    }
  };

  // View details handler — FIXED to use getGroupInfo helper
  const handleViewDetails = async (report, memberData) => {
    try {
      const { groupField, groupValue } = getGroupInfo(report, memberData);

      const detailsResponse = await apiService.getGroupMemberDetails(
        report.id,
        groupField,
        groupValue,
        {},
        selectedPeriod,
      );

      setSelectedReport({
        ...report,
        memberData,
        detailRecords: detailsResponse.data || [],
      });
    } catch (err) {
      console.error("Failed to load details:", err);
      setSelectedReport({
        ...report,
        memberData,
        detailRecords: [],
      });
    }
  };

  // Calculate stats
  const telemarketingStats = {
    totalBooked:
      telemarketingReports.total_booked_appts_tm_current_month?.results
        ?.aggregates?.["Total Booked"] || 0,
    totalQualSat:
      telemarketingReports.qual_sat_performance_tm_current_month?.results
        ?.aggregates?.["Total Qual SAT"] || 0,
    totalSold:
      telemarketingReports.deals_sold_units_tm_current_month?.results
        ?.aggregates?.["Deals Sold"] || 0,
    totalSoldUnits:
      telemarketingReports.deals_sold_units_tm_current_month?.results
        ?.aggregates?.["Total Units Sold"] || 0,
    totalInstalled:
      telemarketingReports.deals_installed_units_tm_current_month?.results
        ?.aggregates?.["Deals Installed"] || 0,
    totalInstalledRevenue:
      telemarketingReports.deals_installed_units_tm_current_month?.results
        ?.aggregates?.["Total Amount"] || 0,
    totalInstalledUnits:
      telemarketingReports.deals_installed_units_tm_current_month?.results
        ?.aggregates?.["Total Units Installed"] || 0,
    totalRevenue:
      telemarketingReports.deals_sold_units_tm_current_month?.results
        ?.aggregates?.["Total Amount"] || 0,
    totalUnits:
      telemarketingReports.deals_sold_units_tm_current_month?.results
        ?.aggregates?.["Total Units Sold"] || 0,
    totalCancelled:
      telemarketingReports.cancelled_appointments_tm_current_month?.results
        ?.aggregates?.["Total Cancelled"] || 0,
    avgDaysToCancel: (() => {
      const data =
        telemarketingReports.cancelled_appointments_tm_current_month?.results
          ?.data || [];
      const vals = data
        .map((r) => parseFloat(r["Avg Business Days to Cancel"]))
        .filter((v) => !isNaN(v));
      if (vals.length === 0) return 0;
      return vals.reduce((a, b) => a + b, 0) / vals.length;
    })(),
  };
  const salesStats = {
    totalSold:
      salesReports.sold_deals_sales_rep_current_month?.results?.aggregates?.[
        "Total Deals Sold"
      ] || 0,
    totalSoldUnits:
      salesReports.sold_deals_sales_rep_current_month?.results?.aggregates?.[
        "Total Units Sold"
      ] || 0,
    totalRevenue:
      salesReports.sold_deals_sales_rep_current_month?.results?.aggregates?.[
        "Total Amount"
      ] || 0,
    totalInstalled:
      salesReports.installed_sales_rep_current_month?.results?.aggregates?.[
        "Total Installations"
      ] || 0,
    totalInstalledUnits:
      salesReports.installed_sales_rep_current_month?.results?.aggregates?.[
        "Total Units Installed"
      ] || 0,
    totalInstalledRevenue:
      salesReports.installed_sales_rep_current_month?.results?.aggregates?.[
        "Total Amount"
      ] || 0,
    totalQualSat:
      salesReports.qual_sat_percentage_sales_rep_current_month?.results
        ?.aggregates?.["Total Qual SAT"] || 0,
    totalAppointments:
      salesReports.appointments_booked_sales_rep_current_month?.results
        ?.aggregates?.["Total SAT Appointments"] || 0,

    // NEW: Calculate average Qual SAT % across all sales reps
    avgQualSatPercentage: (() => {
      const data =
        salesReports.qual_sat_percentage_sales_rep_current_month?.results
          ?.data || [];
      if (data.length === 0) return 0;

      const percentages = data.map((rep) => {
        const qualSat = parseFloat(rep["Total Qual SAT"]) || 0;
        const nonQualSat = parseFloat(rep["Total Non-Qual SAT"]) || 0;
        const total = qualSat + nonQualSat;
        return total > 0 ? (qualSat / total) * 100 : 0;
      });

      const sum = percentages.reduce((acc, val) => acc + val, 0);
      return percentages.length > 0 ? sum / percentages.length : 0;
    })(),

    // NEW: Calculate average Close Rate % across all sales reps
    avgCloseRate: (() => {
      const data =
        salesReports.qual_sat_percentage_sales_rep_current_month?.results
          ?.data || [];
      if (data.length === 0) return 0;

      const closeRates = data.map((rep) => {
        const sold = parseFloat(rep["Total Sold"]) || 0;
        const qualSat = parseFloat(rep["Total Qual SAT"]) || 0;
        return qualSat > 0 ? (sold / qualSat) * 100 : 0;
      });

      const sum = closeRates.reduce((acc, val) => acc + val, 0);
      return closeRates.length > 0 ? sum / closeRates.length : 0;
    })(),
  };

  const calculateQualSatPercentage = (qualSat, nonQualSat) => {
    const total = qualSat + nonQualSat;
    return total > 0 ? ((qualSat / total) * 100).toFixed(1) : "0.0";
  };

  // Loading state
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-96">
        <div className="text-center">
          <RefreshCw
            size={48}
            className="text-blue-500 animate-spin mx-auto mb-4"
          />
          <p className={isDarkMode ? "text-gray-400" : "text-gray-500"}>
            Loading sales dashboard...
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4 sm:space-y-6 p-3 sm:p-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1
            className={`text-xl sm:text-2xl font-bold ${isDarkMode ? "text-white" : "text-gray-900"}`}
          >
            Sales Performance Dashboard
          </h1>
          {currentMonthPeriod && selectedPeriod === 0 && (
            <p
              className={`text-sm mt-1 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
            >
              Current Period: {currentMonthPeriod.periodLabel}
            </p>
          )}
        </div>

        <div className="flex items-center gap-2 sm:gap-3 flex-wrap">
          {/* Period Selector */}
          {availablePeriods.length > 0 && (
            <select
              value={selectedPeriod}
              onChange={(e) => setSelectedPeriod(parseInt(e.target.value))}
              className={`px-2 sm:px-4 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${isDarkMode ? "bg-gray-800 border-gray-700 text-white" : "bg-white border-gray-300 text-gray-900"}`}
            >
              {availablePeriods.map((period) => (
                <option key={period.offset} value={period.offset}>
                  {period.label}
                </option>
              ))}
            </select>
          )}

          {lastRefresh && (
            <span
              className={`text-xs hidden sm:flex items-center gap-1 ${isDarkMode ? "text-gray-500" : "text-gray-400"}`}
            >
              <Clock size={12} />
              Updated {lastRefresh.toLocaleTimeString()}
            </span>
          )}
          <button
            onClick={() => loadDashboardData(true, true)}
            disabled={refreshing}
            className="flex items-center gap-1 sm:gap-2 px-3 sm:px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-800 rounded-lg text-white text-sm transition-colors"
          >
            <RefreshCw size={16} className={refreshing ? "animate-spin" : ""} />
            <span className="hidden xs:inline">
              {refreshing ? "Refreshing..." : "Refresh"}
            </span>
          </button>
        </div>
      </div>

      {/* Error State */}
      {error && (
        <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-4 text-red-400">
          {error}
        </div>
      )}

      {/* Team Tabs */}
      <div
        className={`flex gap-1 border-b ${isDarkMode ? "border-gray-800" : "border-gray-200"}`}
      >
        <button
          onClick={() => setActiveTab("telemarketing")}
          className={`px-3 sm:px-6 py-3 font-medium text-sm transition-colors border-b-2 -mb-px ${
            activeTab === "telemarketing"
              ? "text-blue-400 border-blue-400 bg-blue-500/5"
              : isDarkMode
                ? "text-gray-400 border-transparent hover:text-gray-300 hover:bg-gray-800/50"
                : "text-gray-500 border-transparent hover:text-gray-700 hover:bg-gray-100"
          }`}
        >
          <div className="flex items-center gap-1 sm:gap-2">
            <Phone size={16} />
            <span>Telemarketing</span>
          </div>
        </button>
        <button
          onClick={() => setActiveTab("sales")}
          className={`px-3 sm:px-6 py-3 font-medium text-sm transition-colors border-b-2 -mb-px ${
            activeTab === "sales"
              ? "text-blue-400 border-blue-400 bg-blue-500/5"
              : isDarkMode
                ? "text-gray-400 border-transparent hover:text-gray-300 hover:bg-gray-800/50"
                : "text-gray-500 border-transparent hover:text-gray-700 hover:bg-gray-100"
          }`}
        >
          <div className="flex items-center gap-1 sm:gap-2">
            <Users size={16} />
            <span>Sales Team</span>
          </div>
        </button>
      </div>

      {/* Telemarketing Dashboard */}
      {activeTab === "telemarketing" && (
        <div className="space-y-4 sm:space-y-6">
          {/* Stats Grid */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4">
            <StatCard
              title="Booked"
              value={formatNumber(telemarketingStats.totalBooked)}
              icon={Calendar}
              color="blue"
              isDarkMode={isDarkMode}
            />
            <StatCard
              title="Qual SAT"
              value={formatNumber(telemarketingStats.totalQualSat)}
              icon={CheckCircle}
              color="green"
              isDarkMode={isDarkMode}
            />
            <StatCard
              title="Sold"
              value={formatNumber(telemarketingStats.totalSold)}
              icon={Award}
              color="purple"
              isDarkMode={isDarkMode}
            />
            <StatCard
              title="Sold Units"
              value={formatNumber(telemarketingStats.totalSoldUnits)}
              icon={BarChart3}
              color="cyan"
              isDarkMode={isDarkMode}
            />
            <StatCard
              title="Sold Revenue"
              value={formatCurrency(telemarketingStats.totalRevenue)}
              icon={DollarSign}
              color="emerald"
              isDarkMode={isDarkMode}
            />
            <StatCard
              title="Installed"
              value={formatNumber(telemarketingStats.totalInstalled)}
              icon={Package}
              color="indigo"
              isDarkMode={isDarkMode}
            />
            <StatCard
              title="Installed Units"
              value={formatNumber(telemarketingStats.totalInstalledUnits)}
              icon={Package}
              color="cyan"
              isDarkMode={isDarkMode}
            />
            <StatCard
              title="Installed Revenue"
              value={formatCurrency(telemarketingStats.totalInstalledRevenue)}
              icon={DollarSign}
              color="emerald"
              isDarkMode={isDarkMode}
            />
            <StatCard
              title="Cancelled"
              value={formatNumber(telemarketingStats.totalCancelled)}
              icon={XCircle}
              color="orange"
              isDarkMode={isDarkMode}
            />
            <StatCard
              title="Avg Time to Cancel"
              value={`${telemarketingStats.avgDaysToCancel.toFixed(1)}d`}
              subtitle="Booked → cancelled"
              icon={Clock}
              color="orange"
              isDarkMode={isDarkMode}
            />
          </div>

          {/* Reports Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
            {telemarketingReports.total_booked_appts_tm_current_month && (
              <ReportCard
                report={
                  telemarketingReports.total_booked_appts_tm_current_month
                }
                onViewDetails={(data) =>
                  handleViewDetails(
                    telemarketingReports.total_booked_appts_tm_current_month,
                    data,
                  )
                }
                onExport={(format) =>
                  handleExportReport(
                    "total_booked_appts_tm_current_month",
                    "telemarketing",
                    format,
                  )
                }
                isDarkMode={isDarkMode}
              />
            )}

            {telemarketingReports.qual_sat_performance_tm_current_month && (
              <ReportCard
                report={
                  telemarketingReports.qual_sat_performance_tm_current_month
                }
                showPercentage
                onViewDetails={(data) =>
                  handleViewDetails(
                    telemarketingReports.qual_sat_performance_tm_current_month,
                    data,
                  )
                }
                onExport={(format) =>
                  handleExportReport(
                    "qual_sat_performance_tm_current_month",
                    "base_qual_sat_performance_tm_current_month",
                    "new_business_qual_sat_performance_tm_current_month",
                    "telemarketing",
                    format,
                  )
                }
                isDarkMode={isDarkMode}
              />
            )}

            {telemarketingReports.base_qual_sat_performance_tm_current_month && (
              <ReportCard
                report={
                  telemarketingReports.base_qual_sat_performance_tm_current_month
                }
                showCloseRate
                onViewDetails={(data) =>
                  handleViewDetails(
                    telemarketingReports.base_qual_sat_performance_tm_current_month,
                    data,
                  )
                }
                onExport={(format) =>
                  handleExportReport(
                    "base_qual_sat_performance_tm_current_month",
                    "telemarketing",
                    format,
                  )
                }
                isDarkMode={isDarkMode}
              />
            )}

            {telemarketingReports.new_business_qual_sat_performance_tm_current_month && (
              <ReportCard
                report={
                  telemarketingReports.new_business_qual_sat_performance_tm_current_month
                }
                showCloseRate
                onViewDetails={(data) =>
                  handleViewDetails(
                    telemarketingReports.new_business_qual_sat_performance_tm_current_month,
                    data,
                  )
                }
                onExport={(format) =>
                  handleExportReport(
                    "new_business_qual_sat_performance_tm_current_month",
                    "telemarketing",
                    format,
                  )
                }
                isDarkMode={isDarkMode}
              />
            )}

            {telemarketingReports.deals_sold_units_tm_current_month && (
              <ReportCard
                report={telemarketingReports.deals_sold_units_tm_current_month}
                onViewDetails={(data) =>
                  handleViewDetails(
                    telemarketingReports.deals_sold_units_tm_current_month,
                    data,
                  )
                }
                onExport={(format) =>
                  handleExportReport(
                    "deals_sold_units_tm_current_month",
                    "telemarketing",
                    format,
                  )
                }
                isDarkMode={isDarkMode}
              />
            )}

            {telemarketingReports.deals_installed_units_tm_current_month && (
              <ReportCard
                report={
                  telemarketingReports.deals_installed_units_tm_current_month
                }
                onViewDetails={(data) =>
                  handleViewDetails(
                    telemarketingReports.deals_installed_units_tm_current_month,
                    data,
                  )
                }
                onExport={(format) =>
                  handleExportReport(
                    "deals_installed_units_tm_current_month",
                    "telemarketing",
                    format,
                  )
                }
                isDarkMode={isDarkMode}
              />
            )}
          </div>
          {telemarketingReports.cancelled_appointments_tm_current_month && (
            <CancelledAppointmentsReport
              report={
                telemarketingReports.cancelled_appointments_tm_current_month
              }
              groupLabel="Telemarketer"
              onViewDetails={(data) =>
                handleViewDetails(
                  telemarketingReports.cancelled_appointments_tm_current_month,
                  data,
                )
              }
              onExport={(format) =>
                handleExportReport(
                  "cancelled_appointments_tm_current_month",
                  "telemarketing",
                  format,
                )
              }
              isDarkMode={isDarkMode}
            />
          )}
          {telemarketingReports.coach_performance_current_month && (
            <CoachPerformanceReport
              report={telemarketingReports.coach_performance_current_month}
              onViewDetails={(data) =>
                handleViewDetails(
                  telemarketingReports.coach_performance_current_month,
                  data,
                )
              }
              onExport={(format) =>
                handleExportReport(
                  "coach_performance_current_month",
                  "telemarketing",
                  format,
                )
              }
              isDarkMode={isDarkMode}
            />
          )}

          {/* Day by Day Booking */}
          {telemarketingReports.day_by_day_booking_current_month && (
            <DayByDayReport
              report={telemarketingReports.day_by_day_booking_current_month}
              onExport={(format) =>
                handleExportReport(
                  "day_by_day_booking_current_month",
                  "telemarketing",
                  format,
                )
              }
              isDarkMode={isDarkMode}
            />
          )}
        </div>
      )}

      {/* Sales Team Dashboard */}
      {activeTab === "sales" && (
        <div className="space-y-4 sm:space-y-6">
          {/* Stats Grid */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4">
            <StatCard
              title="Appointments"
              value={formatNumber(salesStats.totalAppointments)}
              icon={Calendar}
              color="blue"
              isDarkMode={isDarkMode}
            />
            <StatCard
              title="Qual SAT"
              value={formatNumber(salesStats.totalQualSat)}
              icon={CheckCircle}
              color="green"
              isDarkMode={isDarkMode}
            />
            <StatCard
              title="Avg Qual SAT %"
              value={formatPercent(salesStats.avgQualSatPercentage)}
              subtitle="Team Average"
              icon={Percent}
              color="green"
              isDarkMode={isDarkMode}
            />
            {/* NEW: Average Close Rate Card */}
            <StatCard
              title="Avg Close Rate"
              value={formatPercent(salesStats.avgCloseRate)}
              subtitle="Team Average"
              icon={Target}
              color="cyan"
              isDarkMode={isDarkMode}
            />
            <StatCard
              title="Sold"
              value={formatNumber(salesStats.totalSold)}
              icon={Award}
              color="purple"
              isDarkMode={isDarkMode}
            />
            <StatCard
              title="Sold Units"
              value={formatNumber(salesStats.totalSoldUnits)}
              icon={Award}
              color="purple"
              isDarkMode={isDarkMode}
            />
            <StatCard
              title="Sold Revenue"
              value={formatCurrency(salesStats.totalRevenue)}
              icon={DollarSign}
              color="emerald"
              isDarkMode={isDarkMode}
            />
            <StatCard
              title="Installed"
              value={formatNumber(salesStats.totalInstalled)}
              icon={Package}
              color="indigo"
              isDarkMode={isDarkMode}
            />
            <StatCard
              title="Installed Units"
              value={formatNumber(salesStats.totalInstalledUnits)}
              icon={Award}
              color="purple"
              isDarkMode={isDarkMode}
            />
            <StatCard
              title="Installed Revenue"
              value={formatCurrency(salesStats.totalInstalledRevenue)}
              icon={DollarSign}
              color="emerald"
              isDarkMode={isDarkMode}
            />
          </div>

          {/* Reports Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
            {salesReports.appointments_booked_sales_rep_current_month && (
              <ReportCard
                report={
                  salesReports.appointments_booked_sales_rep_current_month
                }
                showPercentage
                onViewDetails={(data) =>
                  handleViewDetails(
                    salesReports.appointments_booked_sales_rep_current_month,
                    data,
                  )
                }
                onExport={(format) =>
                  handleExportReport(
                    "appointments_booked_sales_rep_current_month",
                    "sales",
                    format,
                  )
                }
                isDarkMode={isDarkMode}
              />
            )}

            {salesReports.qual_sat_percentage_sales_rep_current_month && (
              <ReportCard
                report={
                  salesReports.qual_sat_percentage_sales_rep_current_month
                }
                showCloseRate
                onViewDetails={(data) =>
                  handleViewDetails(
                    salesReports.qual_sat_percentage_sales_rep_current_month,
                    data,
                  )
                }
                onExport={(format) =>
                  handleExportReport(
                    "qual_sat_percentage_sales_rep_current_month",
                    "sales",
                    format,
                  )
                }
                isDarkMode={isDarkMode}
              />
            )}

            {salesReports.base_qual_sat_performance_sales_rep_current_month && (
              <ReportCard
                report={
                  salesReports.base_qual_sat_performance_sales_rep_current_month
                }
                showCloseRate
                onViewDetails={(data) =>
                  handleViewDetails(
                    salesReports.base_qual_sat_performance_sales_rep_current_month,
                    data,
                  )
                }
                onExport={(format) =>
                  handleExportReport(
                    "base_qual_sat_performance_sales_rep_current_month",
                    "sales",
                    format,
                  )
                }
                isDarkMode={isDarkMode}
              />
            )}

            {salesReports.new_business_qual_sat_performance_sales_rep_current_month && (
              <ReportCard
                report={
                  salesReports.new_business_qual_sat_performance_sales_rep_current_month
                }
                showCloseRate
                onViewDetails={(data) =>
                  handleViewDetails(
                    salesReports.new_business_qual_sat_performance_sales_rep_current_month,
                    data,
                  )
                }
                onExport={(format) =>
                  handleExportReport(
                    "new_business_qual_sat_performance_sales_rep_current_month",
                    "sales",
                    format,
                  )
                }
                isDarkMode={isDarkMode}
              />
            )}

            {salesReports.sold_deals_sales_rep_current_month && (
              <ReportCard
                report={salesReports.sold_deals_sales_rep_current_month}
                onViewDetails={(data) =>
                  handleViewDetails(
                    salesReports.sold_deals_sales_rep_current_month,
                    data,
                  )
                }
                onExport={(format) =>
                  handleExportReport(
                    "sold_deals_sales_rep_current_month",
                    "sales",
                    format,
                  )
                }
                isDarkMode={isDarkMode}
              />
            )}

            {salesReports.installed_sales_rep_current_month && (
              <ReportCard
                report={salesReports.installed_sales_rep_current_month}
                onViewDetails={(data) =>
                  handleViewDetails(
                    salesReports.installed_sales_rep_current_month,
                    data,
                  )
                }
                onExport={(format) =>
                  handleExportReport(
                    "installed_sales_rep_current_month",
                    "sales",
                    format,
                  )
                }
                isDarkMode={isDarkMode}
              />
            )}
          </div>

          {/* Coach Performance */}

          {/* Sale Originated Breakdown */}
          {salesReports.sale_originated_breakdown_current_month && (
            <SaleOriginatedReport
              report={salesReports.sale_originated_breakdown_current_month}
              onExport={(format) =>
                handleExportReport(
                  "sale_originated_breakdown_current_month",
                  "sales",
                  format,
                )
              }
              isDarkMode={isDarkMode}
            />
          )}
        </div>
      )}

      {/* Detail Modal */}
      {selectedReport && (
        <DetailModal
          report={selectedReport}
          onClose={() => setSelectedReport(null)}
          isDarkMode={isDarkMode}
        />
      )}
    </div>
  );
};

// ============================================
// STAT CARD COMPONENT
// ============================================

const SALE_ORIGINATED_LABELS = {
  cold_call: "Cold Call",
  reschedule: "Reschedule",
  email: "Email",
  base: "Base",
  website_enquiry: "Website Enquiry",
  incoming_call: "Incoming Call",
  not_set: "Not Set",
};

const SALE_ORIGINATED_COLORS = [
  {
    bar: "#427BCA",
    bg: "bg-blue-500/20",
    text: "text-blue-400",
    border: "border-blue-500/30",
  },
  {
    bar: "#10B981",
    bg: "bg-emerald-500/20",
    text: "text-emerald-400",
    border: "border-emerald-500/30",
  },
  {
    bar: "#8B5CF6",
    bg: "bg-purple-500/20",
    text: "text-purple-400",
    border: "border-purple-500/30",
  },
  {
    bar: "#F59E0B",
    bg: "bg-amber-500/20",
    text: "text-amber-400",
    border: "border-amber-500/30",
  },
  {
    bar: "#EF4444",
    bg: "bg-red-500/20",
    text: "text-red-400",
    border: "border-red-500/30",
  },
  {
    bar: "#06B6D4",
    bg: "bg-cyan-500/20",
    text: "text-cyan-400",
    border: "border-cyan-500/30",
  },
  {
    bar: "#6B7280",
    bg: "bg-gray-500/20",
    text: "text-gray-400",
    border: "border-gray-500/30",
  },
];

const SaleOriginatedReport = ({ report, onExport, isDarkMode }) => {
  const [showExportMenu, setShowExportMenu] = useState(false);
  const exportMenuRef = useRef(null);
  const rows = report?.results?.data || [];

  // Close export menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        exportMenuRef.current &&
        !exportMenuRef.current.contains(event.target)
      ) {
        setShowExportMenu(false);
      }
    };
    if (showExportMenu) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showExportMenu]);

  // Calculate total and inject % of Total
  const grandTotal = rows.reduce(
    (sum, r) => sum + (parseInt(r["Total"]) || 0),
    0,
  );
  const enriched = rows.map((r, i) => ({
    ...r,
    label:
      SALE_ORIGINATED_LABELS[r.saleOriginated] || r.saleOriginated || "Unknown",
    count: parseInt(r["Total"]) || 0,
    pct: grandTotal > 0 ? ((parseInt(r["Total"]) || 0) / grandTotal) * 100 : 0,
    color: SALE_ORIGINATED_COLORS[i % SALE_ORIGINATED_COLORS.length],
  }));

  const maxCount = Math.max(...enriched.map((r) => r.count), 1);

  if (!report) return null;

  return (
    <div
      className={`rounded-xl border overflow-hidden hover:border-gray-700 transition-colors ${
        isDarkMode ? "bg-[#0d0f14] border-gray-800" : "bg-white border-gray-200"
      }`}
    >
      {/* Header */}
      <div
        className={`p-4 border-b flex items-start justify-between ${
          isDarkMode ? "border-gray-800" : "border-gray-200"
        }`}
      >
        <div className="flex-1 min-w-0 pr-4">
          <h3
            className={`text-base font-semibold truncate ${
              isDarkMode ? "text-white" : "text-gray-900"
            }`}
          >
            {report.name}
          </h3>
          <p
            className={`text-xs mt-1 line-clamp-1 ${isDarkMode ? "text-gray-500" : "text-gray-400"}`}
          >
            {report.description}
          </p>
        </div>

        {/* Export dropdown */}
        {onExport && (
          <div className="relative" ref={exportMenuRef}>
            <button
              onClick={() => setShowExportMenu(!showExportMenu)}
              className={`p-2 rounded-lg transition-colors ${
                isDarkMode
                  ? "text-gray-400 hover:text-white hover:bg-gray-800"
                  : "text-gray-500 hover:text-gray-900 hover:bg-gray-100"
              }`}
              title="Export Report"
            >
              <Download size={16} />
            </button>

            {showExportMenu && (
              <div
                className={`absolute right-0 mt-2 w-36 rounded-lg shadow-2xl z-50 overflow-hidden ${
                  isDarkMode
                    ? "bg-gray-900 border border-gray-700"
                    : "bg-white border border-gray-200"
                }`}
              >
                <div className="py-1">
                  <button
                    onClick={() => {
                      onExport("csv");
                      setShowExportMenu(false);
                    }}
                    className={`w-full px-4 py-2.5 text-left text-sm transition-colors flex items-center gap-2 ${
                      isDarkMode
                        ? "text-gray-300 hover:bg-gray-800 hover:text-white"
                        : "text-gray-700 hover:bg-gray-100 hover:text-gray-900"
                    }`}
                  >
                    <Download size={14} />
                    <span>Export CSV</span>
                  </button>
                  <div
                    className={`h-px ${isDarkMode ? "bg-gray-800" : "bg-gray-200"}`}
                  ></div>
                  <button
                    onClick={() => {
                      onExport("pdf");
                      setShowExportMenu(false);
                    }}
                    className={`w-full px-4 py-2.5 text-left text-sm transition-colors flex items-center gap-2 ${
                      isDarkMode
                        ? "text-gray-300 hover:bg-gray-800 hover:text-white"
                        : "text-gray-700 hover:bg-gray-100 hover:text-gray-900"
                    }`}
                  >
                    <Download size={14} />
                    <span>Export PDF</span>
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Content */}
      <div className="p-3 sm:p-4">
        {enriched.length === 0 ? (
          <div
            className={`text-center py-8 ${
              isDarkMode ? "text-gray-500" : "text-gray-400"
            }`}
          >
            <BarChart3 size={32} className="mx-auto mb-2 opacity-50" />
            <p className="text-sm">No data available</p>
          </div>
        ) : (
          <>
            {/* Total badge */}
            <div className="flex items-center gap-2 mb-5">
              <span
                className={`text-xs font-medium px-2.5 py-1 rounded-full ${
                  isDarkMode
                    ? "bg-blue-500/10 text-blue-400 border border-blue-500/20"
                    : "bg-blue-50 text-blue-600 border border-blue-200"
                }`}
              >
                {grandTotal} total booked
              </span>
            </div>

            {/* Bar chart rows */}
            <div className="space-y-3">
              {enriched.map((row, i) => (
                <div key={row.saleOriginated || i}>
                  <div className="flex items-center justify-between mb-1 gap-2">
                    <span
                      className={`text-sm font-medium truncate ${
                        isDarkMode ? "text-gray-200" : "text-gray-800"
                      }`}
                    >
                      {row.label}
                    </span>
                    <div className="flex items-center gap-3 flex-shrink-0">
                      <span
                        className={`text-sm font-semibold ${
                          isDarkMode ? "text-white" : "text-gray-900"
                        }`}
                      >
                        {row.count}
                      </span>
                      <span
                        className={`text-xs w-14 text-right font-medium ${
                          isDarkMode ? "text-gray-400" : "text-gray-500"
                        }`}
                      >
                        {row.pct.toFixed(1)}%
                      </span>
                    </div>
                  </div>
                  <div
                    className={`h-2 rounded-full w-full ${
                      isDarkMode ? "bg-gray-800" : "bg-gray-100"
                    }`}
                  >
                    <div
                      className="h-2 rounded-full transition-all duration-500"
                      style={{
                        width: `${(row.count / maxCount) * 100}%`,
                        backgroundColor: row.color.bar,
                      }}
                    />
                  </div>
                </div>
              ))}
            </div>

            {/* Summary chips */}
            <div
              className={`flex flex-wrap gap-2 mt-5 pt-4 border-t ${
                isDarkMode ? "border-gray-800" : "border-gray-200"
              }`}
            >
              {enriched.map((row, i) => (
                <span
                  key={row.saleOriginated || i}
                  className={`inline-flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-full border ${row.color.bg} ${row.color.text} ${row.color.border}`}
                >
                  <span
                    className="inline-block w-1.5 h-1.5 rounded-full"
                    style={{ backgroundColor: row.color.bar }}
                  />
                  {row.label}: {row.pct.toFixed(1)}%
                </span>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

const StatCard = ({
  title,
  value,
  subtitle,
  icon: Icon,
  color = "blue",
  isDarkMode,
}) => {
  const colorConfig = {
    blue: {
      bg: "from-blue-500/10 to-blue-600/5",
      border: "border-blue-500/20",
      text: "text-blue-400",
    },
    green: {
      bg: "from-green-500/10 to-green-600/5",
      border: "border-green-500/20",
      text: "text-green-400",
    },
    orange: {
      bg: "from-orange-500/10 to-orange-600/5",
      border: "border-orange-500/20",
      text: "text-orange-400",
    },
    purple: {
      bg: "from-purple-500/10 to-purple-600/5",
      border: "border-purple-500/20",
      text: "text-purple-400",
    },
    indigo: {
      bg: "from-indigo-500/10 to-indigo-600/5",
      border: "border-indigo-500/20",
      text: "text-indigo-400",
    },
    cyan: {
      bg: "from-cyan-500/10 to-cyan-600/5",
      border: "border-cyan-500/20",
      text: "text-cyan-400",
    },
    emerald: {
      bg: "from-emerald-500/10 to-emerald-600/5",
      border: "border-emerald-500/20",
      text: "text-emerald-400",
    },
  };

  const colors = colorConfig[color] || colorConfig.blue;

  return (
    <div
      className={`bg-gradient-to-br ${colors.bg} border ${colors.border} rounded-xl p-3 sm:p-4 hover:scale-105 transition-transform duration-200`}
    >
      <div className="flex items-start justify-between mb-3">
        <span
          className={`text-xs font-medium uppercase tracking-wide ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
        >
          {title}
        </span>
        <Icon size={16} className={colors.text} />
      </div>
      <div
        className={`text-lg sm:text-2xl font-bold break-all ${isDarkMode ? "text-white" : "text-gray-900"}`}
      >
        {value}
      </div>
      {subtitle && (
        <div className={`text-sm ${colors.text} mt-1 font-medium`}>
          {subtitle}
        </div>
      )}
    </div>
  );
};

// ============================================
// REPORT CARD COMPONENT
// ============================================

// Add this to SalesDashboard.jsx - COMPLETE ReportCard Component

const ReportCard = ({
  report,
  showPercentage = false,
  showCloseRate = false,
  onViewDetails,
  onExport,
  isDarkMode,
}) => {
  const [showExportMenu, setShowExportMenu] = useState(false);
  const exportMenuRef = useRef(null);
  const data = report.results?.data || [];

  // Close export menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        exportMenuRef.current &&
        !exportMenuRef.current.contains(event.target)
      ) {
        setShowExportMenu(false);
      }
    };

    if (showExportMenu) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showExportMenu]);

  const getMemberName = (row) => {
    // Use the report's groupBy to determine the correct name field
    const groupBy = report.config?.groupBy;

    switch (groupBy) {
      case "assignedTo":
        return row.assignedUser?.name || row["assignedUser.name"] || "Unknown";
      case "coachId":
        return row.coach?.name || row["coach.name"] || "Unknown";
      case "telesalesPerson":
      case "bookedDate":
      default:
        return (
          row.telesalesPerson ||
          row.assignedUser?.name ||
          row["assignedUser.name"] ||
          row.coach?.name ||
          row["coach.name"] ||
          "Unknown"
        );
    }
  };

  const getMetrics = (row) => {
    const metrics = [];

    if (row["Total Booked"]) {
      metrics.push({
        label: "Booked",
        value: formatNumber(row["Total Booked"]),
        color: "text-blue-400",
      });
    }
    if (row["Total SAT Appointments"]) {
      metrics.push({
        label: "SAT",
        value: formatNumber(row["Total SAT Appointments"]),
        color: "text-blue-400",
      });
    }
    if (row["Total Qual SAT"] || row["Qual SAT Count"]) {
      const qualSat = row["Total Qual SAT"] || row["Qual SAT Count"] || 0;
      metrics.push({
        label: "Qual",
        value: formatNumber(qualSat),
        color: "text-green-400",
      });
    }
    if (row["Total Non-Qual SAT"] || row["Non-Qual SAT Count"]) {
      const nonQualSat =
        row["Total Non-Qual SAT"] || row["Non-Qual SAT Count"] || 0;
      metrics.push({
        label: "Non-Qual",
        value: formatNumber(nonQualSat),
        color: "text-orange-400",
      });
    }
    if (row["Qual SAT %"] !== undefined) {
      metrics.push({
        label: "Qual %",
        value: formatPercent(row["Qual SAT %"]),
        color: "text-green-400",
      });
    }
    if (row["Deals Sold"] || row["Total Deals Sold"] || row["Total Sold"]) {
      const sold =
        row["Deals Sold"] || row["Total Deals Sold"] || row["Total Sold"] || 0;
      metrics.push({
        label: "Sold",
        value: formatNumber(sold),
        color: "text-purple-400",
      });
    }
    if (showCloseRate && row["Close Rate %"] !== undefined) {
      metrics.push({
        label: "Close Rate",
        value: formatPercent(row["Close Rate %"]),
        color: "text-cyan-400",
      });
    }
    if (row["Total Units Sold"] || row["Total Units Installed"]) {
      const units =
        row["Total Units Sold"] || row["Total Units Installed"] || 0;
      metrics.push({
        label: "Units",
        value: formatNumber(units),
        color: "text-cyan-400",
      });
    }
    if (row["Deals Installed"] || row["Total Installations"]) {
      const installed =
        row["Deals Installed"] || row["Total Installations"] || 0;
      metrics.push({
        label: "Installed",
        value: formatNumber(installed),
        color: "text-indigo-400",
      });
    }
    if (row["Total Amount"]) {
      metrics.push({
        label: "Revenue",
        value: formatCurrency(row["Total Amount"]),
        color: "text-emerald-400",
      });
    }

    return metrics;
  };

  const handleExportClick = (format) => {
    onExport(format);
    setShowExportMenu(false);
  };

  return (
    <div
      className={`rounded-xl border overflow-hidden hover:border-gray-700 transition-colors ${isDarkMode ? "bg-[#0d0f14] border-gray-800" : "bg-white border-gray-200"}`}
    >
      {/* Header */}
      <div
        className={`p-4 border-b flex items-start justify-between ${isDarkMode ? "border-gray-800" : "border-gray-200"}`}
      >
        <div className="flex-1 min-w-0 pr-4">
          <h3
            className={`text-base font-semibold truncate ${isDarkMode ? "text-white" : "text-gray-900"}`}
          >
            {report.name}
          </h3>
          <p
            className={`text-xs mt-1 line-clamp-1 ${isDarkMode ? "text-gray-500" : "text-gray-400"}`}
          >
            {report.description}
          </p>
        </div>

        {/* Export dropdown */}
        <div className="relative" ref={exportMenuRef}>
          <button
            onClick={() => setShowExportMenu(!showExportMenu)}
            className={`p-2 rounded-lg transition-colors ${isDarkMode ? "text-gray-400 hover:text-white hover:bg-gray-800" : "text-gray-500 hover:text-gray-900 hover:bg-gray-100"}`}
            title="Export Report"
          >
            <Download size={16} />
          </button>

          {showExportMenu && (
            <div
              className={`absolute right-0 mt-2 w-36 rounded-lg shadow-2xl z-50 overflow-hidden ${isDarkMode ? "bg-gray-900 border border-gray-700" : "bg-white border border-gray-200"}`}
            >
              <div className="py-1">
                <button
                  onClick={() => handleExportClick("csv")}
                  className={`w-full px-4 py-2.5 text-left text-sm transition-colors flex items-center gap-2 ${isDarkMode ? "text-gray-300 hover:bg-gray-800 hover:text-white" : "text-gray-700 hover:bg-gray-100 hover:text-gray-900"}`}
                >
                  <Download size={14} />
                  <span>Export CSV</span>
                </button>
                <div
                  className={`h-px ${isDarkMode ? "bg-gray-800" : "bg-gray-200"}`}
                ></div>
                <button
                  onClick={() => handleExportClick("pdf")}
                  className={`w-full px-4 py-2.5 text-left text-sm transition-colors flex items-center gap-2 ${isDarkMode ? "text-gray-300 hover:bg-gray-800 hover:text-white" : "text-gray-700 hover:bg-gray-100 hover:text-gray-900"}`}
                >
                  <Download size={14} />
                  <span>Export PDF</span>
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Content */}
      <div className="p-3 sm:p-4 max-h-80 overflow-y-auto">
        {data.length === 0 ? (
          <div
            className={`text-center py-8 ${isDarkMode ? "text-gray-500" : "text-gray-400"}`}
          >
            <BarChart3 size={32} className="mx-auto mb-2 opacity-50" />
            <p className="text-sm">No data available</p>
          </div>
        ) : (
          <div className="space-y-2">
            {data.map((row, idx) => {
              const metrics = getMetrics(row);
              return (
                <button
                  key={idx}
                  onClick={() => onViewDetails(row)}
                  className={`w-full text-left p-3 rounded-lg transition-colors group ${isDarkMode ? "bg-gray-900/40 hover:bg-gray-800/60" : "bg-gray-50 hover:bg-gray-100"}`}
                >
                  <div className="flex items-center justify-between">
                    <span
                      className={`font-medium truncate flex-1 ${isDarkMode ? "text-white" : "text-gray-900"}`}
                    >
                      {getMemberName(row)}
                    </span>
                    <ChevronRight
                      size={16}
                      className={`ml-2 flex-shrink-0 ${isDarkMode ? "text-gray-600 group-hover:text-gray-400" : "text-gray-400 group-hover:text-gray-600"}`}
                    />
                  </div>
                  <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2">
                    {metrics.map((metric, mIdx) => (
                      <span key={mIdx} className="text-xs">
                        <span
                          className={
                            isDarkMode ? "text-gray-500" : "text-gray-400"
                          }
                        >
                          {metric.label}:
                        </span>{" "}
                        <span className={metric.color}>{metric.value}</span>
                      </span>
                    ))}
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </div>

      {/* Footer with totals */}
      {report.results?.aggregates &&
        Object.keys(report.results.aggregates).length > 0 && (
          <div
            className={`p-3 border-t ${isDarkMode ? "bg-gray-900/60 border-gray-800" : "bg-gray-50 border-gray-200"}`}
          >
            <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs">
              {Object.entries(report.results.aggregates).map(([key, value]) => (
                <span key={key}>
                  <span
                    className={isDarkMode ? "text-gray-500" : "text-gray-400"}
                  >
                    {key}:
                  </span>{" "}
                  <span
                    className={`font-medium ${isDarkMode ? "text-white" : "text-gray-900"}`}
                  >
                    {key.toLowerCase().includes("amount") ||
                    key.toLowerCase().includes("revenue")
                      ? formatCurrency(value)
                      : formatNumber(value)}
                  </span>
                </span>
              ))}
            </div>
          </div>
        )}
    </div>
  );
};

// ============================================
// DAY BY DAY REPORT COMPONENT
// ============================================

const DayByDayReport = ({ report, onExport, isDarkMode }) => {
  const [expanded, setExpanded] = useState(true);
  const data = report.results?.data || [];

  // Group by date
  const groupedByDate = data.reduce((acc, row) => {
    const date = row.bookedDate;
    if (!acc[date]) acc[date] = [];
    acc[date].push(row);
    return acc;
  }, {});

  const sortedDates = Object.keys(groupedByDate).sort(
    (a, b) => new Date(b) - new Date(a),
  );

  return (
    <div
      className={`rounded-xl border overflow-hidden ${isDarkMode ? "bg-[#0d0f14] border-gray-800" : "bg-white border-gray-200"}`}
    >
      {/* Header */}
      <div
        className={`p-4 border-b flex items-center justify-between ${isDarkMode ? "border-gray-800" : "border-gray-200"}`}
      >
        <button
          onClick={() => setExpanded(!expanded)}
          className={`flex items-center gap-2 transition-colors ${isDarkMode ? "text-white hover:text-gray-300" : "text-gray-900 hover:text-gray-600"}`}
        >
          {expanded ? <ChevronDown size={20} /> : <ChevronRight size={20} />}
          <h3 className="text-base font-semibold">{report.name}</h3>
        </button>
        <button
          onClick={onExport}
          className={`p-2 rounded-lg transition-colors ${isDarkMode ? "text-gray-400 hover:text-white hover:bg-gray-800" : "text-gray-500 hover:text-gray-900 hover:bg-gray-100"}`}
        >
          <Download size={16} />
        </button>
      </div>

      {/* Content */}
      {expanded && (
        <div className="p-3 sm:p-4 max-h-96 overflow-y-auto">
          {sortedDates.length === 0 ? (
            <div
              className={`text-center py-8 ${isDarkMode ? "text-gray-500" : "text-gray-400"}`}
            >
              <Calendar size={32} className="mx-auto mb-2 opacity-50" />
              <p className="text-sm">No bookings in this period</p>
            </div>
          ) : (
            <div className="space-y-4">
              {sortedDates.map((date) => {
                const dayBookings = groupedByDate[date];
                const totalForDay = dayBookings.reduce(
                  (sum, b) => sum + (parseInt(b.Bookings, 10) || 0),
                  0,
                );

                return (
                  <div
                    key={date}
                    className={`border rounded-lg overflow-hidden ${isDarkMode ? "border-gray-800" : "border-gray-200"}`}
                  >
                    <div
                      className={`px-4 py-2 flex items-center justify-between ${isDarkMode ? "bg-gray-900/60" : "bg-gray-50"}`}
                    >
                      <span
                        className={`text-sm font-medium ${isDarkMode ? "text-white" : "text-gray-900"}`}
                      >
                        {formatDate(date)}
                      </span>
                      <span className="text-sm text-blue-400 font-medium">
                        {totalForDay} bookings
                      </span>
                    </div>
                    <div
                      className={`divide-y ${isDarkMode ? "divide-gray-800" : "divide-gray-200"}`}
                    >
                      {dayBookings.map((booking, idx) => (
                        <div
                          key={idx}
                          className={`px-4 py-2 flex items-center justify-between transition-colors ${isDarkMode ? "hover:bg-gray-800/30" : "hover:bg-gray-50"}`}
                        >
                          <span
                            className={`text-sm ${isDarkMode ? "text-gray-300" : "text-gray-600"}`}
                          >
                            {booking.telesalesPerson}
                          </span>
                          <span
                            className={`text-sm font-medium ${isDarkMode ? "text-white" : "text-gray-900"}`}
                          >
                            {booking.Bookings}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
};
// ============================================
// CANCELLED APPOINTMENTS REPORT COMPONENT
// ============================================

const CancelledAppointmentsReport = ({
  report,
  onViewDetails,
  onExport,
  isDarkMode,
  groupLabel = "Telemarketer",
}) => {
  const [showExportMenu, setShowExportMenu] = useState(false);
  const exportMenuRef = useRef(null);
  const data = report.results?.data || [];
  const groupBy = report.config?.groupBy;

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        exportMenuRef.current &&
        !exportMenuRef.current.contains(event.target)
      ) {
        setShowExportMenu(false);
      }
    };
    if (showExportMenu) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [showExportMenu]);

  const getGroupName = (row) => {
    if (groupBy === "assignedTo") {
      return row["assignedUser.name"] || row.assignedUser?.name || "Unknown";
    }
    return (
      row.telesalesPerson ||
      row["assignedUser.name"] ||
      row.assignedUser?.name ||
      "Unknown"
    );
  };

  return (
    <div
      className={`rounded-xl border overflow-hidden lg:col-span-2 ${
        isDarkMode ? "bg-[#0d0f14] border-gray-800" : "bg-white border-gray-200"
      }`}
    >
      <div
        className={`p-4 border-b flex items-center justify-between ${
          isDarkMode ? "border-gray-800" : "border-gray-200"
        }`}
      >
        <div className="flex-1 min-w-0 pr-4">
          <h3
            className={`text-base font-semibold flex items-center gap-2 ${
              isDarkMode ? "text-white" : "text-gray-900"
            }`}
          >
            <XCircle size={18} className="text-orange-400" />
            <span className="truncate">{report.name}</span>
          </h3>
          <p
            className={`text-xs mt-1 ${
              isDarkMode ? "text-gray-500" : "text-gray-400"
            }`}
          >
            {report.description}
          </p>
        </div>

        <div className="relative" ref={exportMenuRef}>
          <button
            onClick={() => setShowExportMenu(!showExportMenu)}
            className={`p-2 rounded-lg transition-colors ${
              isDarkMode
                ? "text-gray-400 hover:text-white hover:bg-gray-800"
                : "text-gray-500 hover:text-gray-900 hover:bg-gray-100"
            }`}
          >
            <Download size={16} />
          </button>
          {showExportMenu && (
            <div
              className={`absolute right-0 mt-2 w-36 rounded-lg shadow-2xl z-50 overflow-hidden ${
                isDarkMode
                  ? "bg-gray-900 border border-gray-700"
                  : "bg-white border border-gray-200"
              }`}
            >
              <button
                onClick={() => {
                  onExport("csv");
                  setShowExportMenu(false);
                }}
                className={`w-full px-4 py-2.5 text-left text-sm flex items-center gap-2 ${
                  isDarkMode
                    ? "text-gray-300 hover:bg-gray-800"
                    : "text-gray-700 hover:bg-gray-100"
                }`}
              >
                <Download size={14} /> Export CSV
              </button>
              <button
                onClick={() => {
                  onExport("pdf");
                  setShowExportMenu(false);
                }}
                className={`w-full px-4 py-2.5 text-left text-sm flex items-center gap-2 ${
                  isDarkMode
                    ? "text-gray-300 hover:bg-gray-800"
                    : "text-gray-700 hover:bg-gray-100"
                }`}
              >
                <Download size={14} /> Export PDF
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Desktop table */}
      <div className="hidden md:block overflow-x-auto">
        <table className="w-full text-sm">
          <thead className={isDarkMode ? "bg-gray-900/60" : "bg-gray-50"}>
            <tr
              className={`border-b ${
                isDarkMode ? "border-gray-800" : "border-gray-200"
              }`}
            >
              <th
                className={`text-left py-3 px-4 font-medium ${
                  isDarkMode ? "text-gray-400" : "text-gray-500"
                }`}
              >
                {groupLabel}
              </th>
              <th
                className={`text-right py-3 px-4 font-medium ${
                  isDarkMode ? "text-gray-400" : "text-gray-500"
                }`}
              >
                Cancelled
              </th>
              <th
                className={`text-right py-3 px-4 font-medium ${
                  isDarkMode ? "text-gray-400" : "text-gray-500"
                }`}
              >
                With Reason
              </th>
              <th
                className={`text-right py-3 px-4 font-medium ${
                  isDarkMode ? "text-gray-400" : "text-gray-500"
                }`}
              >
                Avg Hours
              </th>
              <th
                className={`text-right py-3 px-4 font-medium ${
                  isDarkMode ? "text-gray-400" : "text-gray-500"
                }`}
              >
                Avg Days
              </th>
              <th className="w-10"></th>
            </tr>
          </thead>
          <tbody
            className={`divide-y ${
              isDarkMode ? "divide-gray-800" : "divide-gray-200"
            }`}
          >
            {data.map((row, idx) => (
              <tr
                key={idx}
                onClick={() => onViewDetails(row)}
                className={`cursor-pointer transition-colors group ${
                  isDarkMode ? "hover:bg-gray-800/40" : "hover:bg-gray-50"
                }`}
              >
                <td
                  className={`py-3 px-4 font-medium ${
                    isDarkMode ? "text-white" : "text-gray-900"
                  }`}
                >
                  {getGroupName(row)}
                </td>
                <td className="py-3 px-4 text-right text-orange-400 font-semibold">
                  {formatNumber(row["Total Cancelled"] || 0)}
                </td>
                <td
                  className={`py-3 px-4 text-right ${
                    isDarkMode ? "text-gray-300" : "text-gray-600"
                  }`}
                >
                  {formatNumber(row["With Reason"] || 0)}
                </td>
                <td
                  className={`py-3 px-4 text-right ${
                    isDarkMode ? "text-gray-300" : "text-gray-600"
                  }`}
                >
                  {row["Avg Business Hours to Cancel"]
                    ? `${parseFloat(row["Avg Business Hours to Cancel"]).toFixed(1)}h`
                    : "-"}
                </td>
                <td
                  className={`py-3 px-4 text-right ${
                    isDarkMode ? "text-gray-300" : "text-gray-600"
                  }`}
                >
                  {row["Avg Business Days to Cancel"]
                    ? `${parseFloat(row["Avg Business Days to Cancel"]).toFixed(2)}d`
                    : "-"}
                </td>
                <td className="py-3 px-4 text-right">
                  <ChevronRight
                    size={16}
                    className={
                      isDarkMode
                        ? "text-gray-600 group-hover:text-gray-400"
                        : "text-gray-400 group-hover:text-gray-600"
                    }
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {data.length === 0 && (
          <div
            className={`text-center py-12 ${
              isDarkMode ? "text-gray-500" : "text-gray-400"
            }`}
          >
            <XCircle size={32} className="mx-auto mb-2 opacity-50" />
            <p className="text-sm">No cancelled appointments in this period</p>
          </div>
        )}
      </div>

      {/* Mobile cards */}
      <div
        className={`md:hidden divide-y ${
          isDarkMode ? "divide-gray-800" : "divide-gray-200"
        }`}
      >
        {data.length === 0 ? (
          <div
            className={`text-center py-12 ${
              isDarkMode ? "text-gray-500" : "text-gray-400"
            }`}
          >
            <XCircle size={32} className="mx-auto mb-2 opacity-50" />
            <p className="text-sm">No cancelled appointments</p>
          </div>
        ) : (
          data.map((row, idx) => (
            <button
              key={idx}
              onClick={() => onViewDetails(row)}
              className={`w-full text-left p-3 ${
                isDarkMode ? "hover:bg-gray-800/40" : "hover:bg-gray-50"
              }`}
            >
              <div className="flex items-center justify-between mb-2">
                <span
                  className={`font-medium ${
                    isDarkMode ? "text-white" : "text-gray-900"
                  }`}
                >
                  {getGroupName(row)}
                </span>
                <span className="text-orange-400 font-semibold">
                  {formatNumber(row["Total Cancelled"] || 0)}
                </span>
              </div>
              <div className="grid grid-cols-3 gap-2 text-xs">
                <div>
                  <span
                    className={isDarkMode ? "text-gray-500" : "text-gray-400"}
                  >
                    With Reason:{" "}
                  </span>
                  <span
                    className={isDarkMode ? "text-gray-300" : "text-gray-600"}
                  >
                    {formatNumber(row["With Reason"] || 0)}
                  </span>
                </div>
                <div>
                  <span
                    className={isDarkMode ? "text-gray-500" : "text-gray-400"}
                  >
                    Avg H:{" "}
                  </span>
                  <span
                    className={isDarkMode ? "text-gray-300" : "text-gray-600"}
                  >
                    {row["Avg Hours to Cancel"]
                      ? `${parseFloat(row["Avg Hours to Cancel"]).toFixed(1)}h`
                      : "-"}
                  </span>
                </div>
                <div>
                  <span
                    className={isDarkMode ? "text-gray-500" : "text-gray-400"}
                  >
                    Avg D:{" "}
                  </span>
                  <span
                    className={isDarkMode ? "text-gray-300" : "text-gray-600"}
                  >
                    {row["Avg Days to Cancel"]
                      ? `${parseFloat(row["Avg Days to Cancel"]).toFixed(2)}d`
                      : "-"}
                  </span>
                </div>
              </div>
            </button>
          ))
        )}
      </div>
    </div>
  );
};
// ============================================
// COACH PERFORMANCE REPORT COMPONENT
// ============================================

const CoachPerformanceReport = ({
  report,
  onViewDetails,
  onExport,
  isDarkMode,
}) => {
  const [showExportMenu, setShowExportMenu] = useState(false);
  const exportMenuRef = useRef(null);
  const data = report.results?.data || [];

  // Close export menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        exportMenuRef.current &&
        !exportMenuRef.current.contains(event.target)
      ) {
        setShowExportMenu(false);
      }
    };

    if (showExportMenu) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showExportMenu]);

  const handleExportClick = (format) => {
    onExport(format);
    setShowExportMenu(false);
  };

  return (
    <div
      className={`rounded-xl border overflow-hidden ${isDarkMode ? "bg-[#0d0f14] border-gray-800" : "bg-white border-gray-200"}`}
    >
      {/* Header */}
      <div
        className={`p-4 border-b flex items-center justify-between ${isDarkMode ? "border-gray-800" : "border-gray-200"}`}
      >
        <h3
          className={`text-base font-semibold ${isDarkMode ? "text-white" : "text-gray-900"}`}
        >
          {report.name}
        </h3>

        {/* Export dropdown */}
        <div className="relative" ref={exportMenuRef}>
          <button
            onClick={() => setShowExportMenu(!showExportMenu)}
            className={`p-2 rounded-lg transition-colors ${isDarkMode ? "text-gray-400 hover:text-white hover:bg-gray-800" : "text-gray-500 hover:text-gray-900 hover:bg-gray-100"}`}
            title="Export Report"
          >
            <Download size={16} />
          </button>

          {showExportMenu && (
            <div
              className={`absolute right-0 mt-2 w-36 rounded-lg shadow-2xl z-50 overflow-hidden ${isDarkMode ? "bg-gray-900 border border-gray-700" : "bg-white border border-gray-200"}`}
            >
              <div className="py-1">
                <button
                  onClick={() => handleExportClick("csv")}
                  className={`w-full px-4 py-2.5 text-left text-sm transition-colors flex items-center gap-2 ${isDarkMode ? "text-gray-300 hover:bg-gray-800 hover:text-white" : "text-gray-700 hover:bg-gray-100 hover:text-gray-900"}`}
                >
                  <Download size={14} />
                  <span>Export CSV</span>
                </button>
                <div
                  className={`h-px ${isDarkMode ? "bg-gray-800" : "bg-gray-200"}`}
                ></div>
                <button
                  onClick={() => handleExportClick("pdf")}
                  className={`w-full px-4 py-2.5 text-left text-sm transition-colors flex items-center gap-2 ${isDarkMode ? "text-gray-300 hover:bg-gray-800 hover:text-white" : "text-gray-700 hover:bg-gray-100 hover:text-gray-900"}`}
                >
                  <Download size={14} />
                  <span>Export PDF</span>
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Mobile card list */}
      <div className="md:hidden">
        {data.length === 0 ? (
          <div
            className={`text-center py-12 ${isDarkMode ? "text-gray-500" : "text-gray-400"}`}
          >
            <Target size={32} className="mx-auto mb-2 opacity-50" />
            <p className="text-sm">No coach data available</p>
          </div>
        ) : (
          <div
            className={`divide-y ${isDarkMode ? "divide-gray-800" : "divide-gray-200"}`}
          >
            {data.map((coach, idx) => {
              const qualSat = parseFloat(coach["Qual SAT Count"]) || 0;
              const nonQualSat = parseFloat(coach["Non-Qual SAT Count"]) || 0;
              const satTotal = qualSat + nonQualSat;
              const percentage =
                coach["Qual SAT %"] !== undefined
                  ? parseFloat(coach["Qual SAT %"]).toFixed(1)
                  : satTotal > 0
                    ? ((qualSat / satTotal) * 100).toFixed(1)
                    : "0.0";

              return (
                <button
                  key={idx}
                  onClick={() => onViewDetails(coach)}
                  className={`w-full text-left p-3 transition-colors ${isDarkMode ? "hover:bg-gray-800/40" : "hover:bg-gray-50"}`}
                >
                  <div className="flex items-center justify-between mb-2">
                    <span
                      className={`font-medium ${isDarkMode ? "text-white" : "text-gray-900"}`}
                    >
                      {coach["coach.name"] || coach.coach?.name || "Unknown"}
                    </span>
                    <ChevronRight
                      size={16}
                      className={isDarkMode ? "text-gray-600" : "text-gray-400"}
                    />
                  </div>
                  <div className="grid grid-cols-3 gap-x-3 gap-y-1 text-xs">
                    <div>
                      <span
                        className={
                          isDarkMode ? "text-gray-500" : "text-gray-400"
                        }
                      >
                        Booked:{" "}
                      </span>
                      <span className="text-blue-400">
                        {formatNumber(coach["Booked"] || 0)}
                      </span>
                    </div>
                    <div>
                      <span
                        className={
                          isDarkMode ? "text-gray-500" : "text-gray-400"
                        }
                      >
                        Qual SAT:{" "}
                      </span>
                      <span className="text-green-400">
                        {formatNumber(qualSat)}
                      </span>
                    </div>
                    <div>
                      <span
                        className={
                          isDarkMode ? "text-gray-500" : "text-gray-400"
                        }
                      >
                        Qual %:{" "}
                      </span>
                      <span
                        className={
                          isDarkMode ? "text-gray-300" : "text-gray-600"
                        }
                      >
                        {percentage}%
                      </span>
                    </div>
                    <div>
                      <span
                        className={
                          isDarkMode ? "text-gray-500" : "text-gray-400"
                        }
                      >
                        Sold:{" "}
                      </span>
                      <span className="text-purple-400">
                        {formatNumber(coach["Deals Sold"] || 0)}
                      </span>
                    </div>
                    <div>
                      <span
                        className={
                          isDarkMode ? "text-gray-500" : "text-gray-400"
                        }
                      >
                        Units:{" "}
                      </span>
                      <span className="text-cyan-400">
                        {formatNumber(coach["Total Units Sold"] || 0)}
                      </span>
                    </div>
                    <div>
                      <span
                        className={
                          isDarkMode ? "text-gray-500" : "text-gray-400"
                        }
                      >
                        Installed:{" "}
                      </span>
                      <span className="text-indigo-400">
                        {formatNumber(coach["Deals Installed"] || 0)}
                      </span>
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </div>

      {/* Desktop table */}
      <div className="hidden md:block overflow-x-auto">
        <table className="w-full text-sm">
          <thead className={isDarkMode ? "bg-gray-900/60" : "bg-gray-50"}>
            <tr
              className={`border-b ${isDarkMode ? "border-gray-800" : "border-gray-200"}`}
            >
              <th
                className={`text-left py-3 px-4 font-medium ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
              >
                Coach
              </th>
              <th
                className={`text-right py-3 px-4 font-medium ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
              >
                Booked
              </th>
              <th
                className={`text-right py-3 px-4 font-medium ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
              >
                Qual SAT
              </th>
              <th
                className={`text-right py-3 px-4 font-medium ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
              >
                Qual %
              </th>
              <th
                className={`text-right py-3 px-4 font-medium ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
              >
                Sold
              </th>
              <th
                className={`text-right py-3 px-4 font-medium ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
              >
                Units Sold
              </th>
              <th
                className={`text-right py-3 px-4 font-medium ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
              >
                Installed
              </th>
              <th
                className={`text-right py-3 px-4 font-medium ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
              >
                Units Installed
              </th>
              <th
                className={`text-right py-3 px-4 font-medium ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
              ></th>
            </tr>
          </thead>
          <tbody
            className={`divide-y ${isDarkMode ? "divide-gray-800" : "divide-gray-200"}`}
          >
            {data.map((coach, idx) => {
              const qualSat = parseFloat(coach["Qual SAT Count"]) || 0;
              const nonQualSat = parseFloat(coach["Non-Qual SAT Count"]) || 0;
              const satTotal = qualSat + nonQualSat;
              const percentage =
                coach["Qual SAT %"] !== undefined
                  ? parseFloat(coach["Qual SAT %"]).toFixed(1)
                  : satTotal > 0
                    ? ((qualSat / satTotal) * 100).toFixed(1)
                    : "0.0";

              return (
                <tr
                  key={idx}
                  onClick={() => onViewDetails(coach)}
                  className={`cursor-pointer transition-colors group ${isDarkMode ? "hover:bg-gray-800/40" : "hover:bg-gray-50"}`}
                >
                  <td
                    className={`py-3 px-4 font-medium ${isDarkMode ? "text-white" : "text-gray-900"}`}
                  >
                    {coach["coach.name"] || coach.coach?.name || "Unknown"}
                  </td>
                  <td className="py-3 px-4 text-right text-blue-400">
                    {formatNumber(coach["Booked"] || 0)}
                  </td>
                  <td className="py-3 px-4 text-right text-green-400">
                    {formatNumber(qualSat)}
                  </td>
                  <td
                    className={`py-3 px-4 text-right ${isDarkMode ? "text-gray-300" : "text-gray-600"}`}
                  >
                    {percentage}%
                  </td>
                  <td className="py-3 px-4 text-right text-purple-400">
                    {formatNumber(coach["Deals Sold"] || 0)}
                  </td>
                  <td className="py-3 px-4 text-right text-cyan-400">
                    {formatNumber(coach["Total Units Sold"] || 0)}
                  </td>
                  <td className="py-3 px-4 text-right text-indigo-400">
                    {formatNumber(coach["Deals Installed"] || 0)}
                  </td>
                  <td className="py-3 px-4 text-right text-cyan-400">
                    {formatNumber(coach["Total Units Installed"] || 0)}
                  </td>
                  <td className="py-3 px-4 text-right">
                    <ChevronRight
                      size={16}
                      className={
                        isDarkMode
                          ? "text-gray-600 group-hover:text-gray-400"
                          : "text-gray-400 group-hover:text-gray-600"
                      }
                    />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>

        {data.length === 0 && (
          <div
            className={`text-center py-12 ${isDarkMode ? "text-gray-500" : "text-gray-400"}`}
          >
            <Target size={32} className="mx-auto mb-2 opacity-50" />
            <p className="text-sm">No coach data available</p>
          </div>
        )}
      </div>
    </div>
  );
};

// ============================================
// DETAIL MODAL COMPONENT (UPDATED)
// ============================================

const DetailModal = ({ report, onClose, isDarkMode }) => {
  const getTimeBetween = (from, to) => {
    if (!from || !to) return null;
    const ms = new Date(to).getTime() - new Date(from).getTime();
    if (isNaN(ms) || ms < 0) return null;
    const totalMinutes = Math.floor(ms / 60000);
    const days = Math.floor(totalMinutes / 1440);
    const hours = Math.floor((totalMinutes % 1440) / 60);
    const minutes = totalMinutes % 60;
    if (days > 0) return `${days}d ${hours}h`;
    if (hours > 0) return `${hours}h ${minutes}m`;
    return `${minutes}m`;
  };
  const memberName =
    report.memberData?.telesalesPerson ||
    report.memberData?.assignedUser?.name ||
    report.memberData?.["assignedUser.name"] ||
    report.memberData?.coach?.name ||
    report.memberData?.["coach.name"] ||
    "Team Member";

  const detailRecords = report.detailRecords || [];

  // Stats from memberData
  const stats = [];
  if (report.memberData?.["Total Booked"]) {
    stats.push({
      label: "Booked",
      value: report.memberData["Total Booked"],
      color: "blue",
    });
  }
  if (
    report.memberData?.["Total Qual SAT"] ||
    report.memberData?.["Qual SAT Count"]
  ) {
    stats.push({
      label: "Qual SAT",
      value:
        report.memberData["Total Qual SAT"] ||
        report.memberData["Qual SAT Count"],
      color: "green",
    });
  }
  if (
    report.memberData?.["Total Non-Qual SAT"] ||
    report.memberData?.["Non-Qual SAT Count"]
  ) {
    stats.push({
      label: "Non-Qual SAT",
      value:
        report.memberData["Total Non-Qual SAT"] ||
        report.memberData["Non-Qual SAT Count"],
      color: "orange",
    });
  }
  if (
    report.memberData?.["Deals Sold"] ||
    report.memberData?.["Total Deals Sold"] ||
    report.memberData?.["Total Sold"]
  ) {
    stats.push({
      label: "Sold",
      value:
        report.memberData["Deals Sold"] ||
        report.memberData["Total Deals Sold"] ||
        report.memberData["Total Sold"],
      color: "purple",
    });
  }
  if (report.memberData?.["Total Units Sold"]) {
    stats.push({
      label: "Units Sold",
      value: report.memberData["Total Units Sold"],
      color: "cyan",
    });
  }
  if (
    report.memberData?.["Deals Installed"] ||
    report.memberData?.["Total Installations"]
  ) {
    stats.push({
      label: "Installed",
      value:
        report.memberData["Deals Installed"] ||
        report.memberData["Total Installations"],
      color: "indigo",
    });
  }
  if (report.memberData?.["Total Units Installed"]) {
    stats.push({
      label: "Units Installed",
      value: report.memberData["Total Units Installed"],
      color: "cyan",
    });
  }
  if (report.memberData?.["Total Amount"]) {
    stats.push({
      label: "Revenue",
      value: report.memberData["Total Amount"],
      color: "emerald",
      isCurrency: true,
    });
  }
  if (report.memberData?.["Total Cancelled"]) {
    stats.push({
      label: "Cancelled",
      value: report.memberData["Total Cancelled"],
      color: "orange",
    });
  }
  if (report.memberData?.["With Reason"]) {
    stats.push({
      label: "With Reason",
      value: report.memberData["With Reason"],
      color: "orange",
    });
  }
  if (report.memberData?.["Avg Business Days to Cancel"] != null) {
    stats.push({
      label: "Avg Business Days",
      value: `${parseFloat(report.memberData["Avg Business Days to Cancel"]).toFixed(2)}d`,
      color: "cyan",
      isRaw: true,
    });
  }
  if (report.memberData?.["Avg Business Hours to Cancel"] != null) {
    stats.push({
      label: "Avg Business Hours",
      value: `${parseFloat(report.memberData["Avg Business Hours to Cancel"]).toFixed(1)}h`,
      color: "cyan",
      isRaw: true,
    });
  }

  // Helper method to format sales status for display
  const formatSalesStatus = (status) => {
    const statusMap = {
      none: "None",
      appointment_booked: "Appointment Booked",
      not_sat: "Not SAT",
      sat_non_qual: "SAT Non-Qual",
      sat_qual: "SAT Qual",
      sold: "Sold",
      dead: "Dead",
      cancelled: "Cancelled",
    };

    return statusMap[status] || status;
  };

  // Helper method to get status badge class (matching your colorConfig)
  const getSalesStatusBadgeClass = (status) => {
    const classMap = {
      none: "bg-gray-500/10 border-gray-500/20",
      appointment_booked: "bg-blue-500/10 border-blue-500/20",
      not_sat: "bg-red-500/10 border-red-500/20",
      sat_non_qual: "bg-orange-500/10 border-orange-500/20",
      sat_qual: "bg-green-500/10 border-green-500/20",
      sold: "bg-emerald-500/10 border-emerald-500/20",
      dead: "bg-slate-500/10 border-slate-500/20",
      cancelled: "bg-red-500/10 border-red-500/20",
    };

    return classMap[status] || "bg-gray-500/10 border-gray-500/20";
  };

  // NEW: Helper to get installation status badge
  const getInstallStatusBadgeClass = (record) => {
    if (!record.sold) return null;

    if (record.installComplete) {
      return {
        text: "Installed",
        class: "bg-emerald-500/10 border-emerald-500/20 text-emerald-400",
        icon: "✅",
      };
    } else if (record.sold && !record.installComplete) {
      return {
        text: "Pending",
        class: "bg-yellow-500/10 border-yellow-500/20 text-yellow-400",
        icon: "⏳",
      };
    }
    return null;
  };

  const colorConfig = {
    blue: "bg-blue-500/10 border-blue-500/20",
    green: "bg-green-500/10 border-green-500/20",
    orange: "bg-orange-500/10 border-orange-500/20",
    purple: "bg-purple-500/10 border-purple-500/20",
    cyan: "bg-cyan-500/10 border-cyan-500/20",
    emerald: "bg-emerald-500/10 border-emerald-500/20",
    indigo: "bg-indigo-500/10 border-indigo-500/20",
  };

  const isCancelledReport =
    report.developerName === "cancelled_appointments_tm_current_month" ||
    report.config?.developerName ===
      "cancelled_appointments_tm_current_month" ||
    (report.memberData?.["Total Cancelled"] != null &&
      report.memberData?.["Avg Business Days to Cancel"] != null);

  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-end sm:items-center justify-center z-50 p-0 sm:p-4">
      <div
        className={`rounded-t-2xl sm:rounded-2xl border max-w-7xl w-full max-h-[95vh] sm:max-h-[90vh] overflow-hidden flex flex-col shadow-2xl ${isDarkMode ? "bg-[#0d0f14] border-gray-800" : "bg-white border-gray-200"}`}
      >
        {/* Header */}
        <div
          className={`p-4 sm:p-6 border-b ${isDarkMode ? "border-gray-800" : "border-gray-200"}`}
        >
          <div className="flex items-start justify-between">
            <div>
              <h2
                className={`text-lg sm:text-xl font-bold ${isDarkMode ? "text-white" : "text-gray-900"}`}
              >
                {report.name}
              </h2>
              <p
                className={
                  isDarkMode ? "text-gray-400 mt-1" : "text-gray-500 mt-1"
                }
              >
                {memberName} - Detail Records
              </p>
            </div>
            <button
              onClick={onClose}
              className={`p-2 rounded-lg transition-colors ${isDarkMode ? "hover:bg-gray-800" : "hover:bg-gray-100"}`}
            >
              <X
                size={20}
                className={isDarkMode ? "text-gray-400" : "text-gray-500"}
              />
            </button>
          </div>

          {/* Summary Stats */}
          {stats.length > 0 && (
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-3 mt-4">
              {stats.map((stat, idx) => (
                <div
                  key={idx}
                  className={`${colorConfig[stat.color]} border rounded-lg p-3`}
                >
                  <div
                    className={`text-xs ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                  >
                    {stat.label}
                  </div>
                  <div
                    className={`text-lg font-bold mt-1 ${isDarkMode ? "text-white" : "text-gray-900"}`}
                  >
                    {stat.isRaw
                      ? stat.value
                      : stat.isCurrency
                        ? formatCurrency(stat.value)
                        : formatNumber(stat.value)}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Detail Records */}
        <div className="flex-1 overflow-auto p-3 sm:p-6">
          {detailRecords.length === 0 ? (
            <div
              className={`text-center py-16 ${isDarkMode ? "text-gray-500" : "text-gray-400"}`}
            >
              <BarChart3 size={48} className="mx-auto mb-4 opacity-50" />
              <p>No detail records available</p>
              <p className="text-sm mt-1">Click refresh to try loading again</p>
            </div>
          ) : (
            <>
              {/* Mobile card list */}
              <div className="flex flex-col gap-3 md:hidden">
                {detailRecords.map((record, idx) => {
                  const installStatus = getInstallStatusBadgeClass(record);
                  const isCancelled = record.salesStatus === "cancelled";
                  const timeToCancel = isCancelled
                    ? getTimeBetween(record.bookedDate, record.cancelledAt)
                    : null;

                  return (
                    <tr
                      key={idx}
                      className={`transition-colors ${
                        isDarkMode ? "hover:bg-gray-800/30" : "hover:bg-gray-50"
                      }`}
                    >
                      <td className="py-3 px-4">
                        <a
                          href={`/opportunities/${record.id}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="font-medium text-blue-400 hover:text-blue-300 hover:underline transition-colors"
                        >
                          {record.name || "Untitled"}
                        </a>
                        {isCancelled && record.cancellationReason && (
                          <div
                            className={`text-xs mt-1 italic ${
                              isDarkMode ? "text-gray-500" : "text-gray-400"
                            }`}
                            title={record.cancellationReason}
                          >
                            Reason:{" "}
                            {record.cancellationReason.length > 60
                              ? `${record.cancellationReason.slice(0, 60)}…`
                              : record.cancellationReason}
                          </div>
                        )}
                      </td>
                      <td
                        className={`py-3 px-4 ${
                          isDarkMode ? "text-gray-300" : "text-gray-600"
                        }`}
                      >
                        {record.account?.name || record["account.name"] || "-"}
                      </td>
                      <td
                        className={`py-3 px-4 ${
                          isDarkMode ? "text-gray-300" : "text-gray-600"
                        }`}
                      >
                        {record.assignedUser?.name ||
                          record["assignedUser.name"] ||
                          "-"}
                      </td>
                      <td
                        className={`py-3 px-4 ${
                          isDarkMode ? "text-gray-300" : "text-gray-600"
                        }`}
                      >
                        {record.telesalesPerson || "-"}
                      </td>
                      <td className="py-3 px-4">
                        <div className="flex flex-col gap-1">
                          <span
                            className={`px-2 py-1 rounded border whitespace-nowrap text-xs ${getSalesStatusBadgeClass(record.salesStatus)}`}
                          >
                            {formatSalesStatus(record.salesStatus)}
                          </span>
                          {isCancelled && timeToCancel && (
                            <span
                              className={`text-xs ${
                                isDarkMode ? "text-gray-500" : "text-gray-400"
                              }`}
                              title="Time between booked and cancelled"
                            >
                              after {timeToCancel}
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="py-3 px-4">
                        {installStatus ? (
                          <span
                            className={`px-2 py-1 rounded border whitespace-nowrap text-xs ${installStatus.class}`}
                          >
                            {installStatus.text}
                          </span>
                        ) : (
                          <span
                            className={`text-xs ${
                              isDarkMode ? "text-gray-500" : "text-gray-400"
                            }`}
                          >
                            -
                          </span>
                        )}
                      </td>
                      <td
                        className={`py-3 px-4 text-right font-medium ${
                          isDarkMode ? "text-white" : "text-gray-900"
                        }`}
                      >
                        {record.amount ? formatCurrency(record.amount) : "-"}
                      </td>
                      <td
                        className={`py-3 px-4 text-right ${
                          isDarkMode ? "text-gray-300" : "text-gray-600"
                        }`}
                      >
                        {record.unitsSold || "-"}
                      </td>
                      <td
                        className={`py-3 px-4 ${
                          isDarkMode ? "text-gray-300" : "text-gray-600"
                        }`}
                      >
                        {formatDate(
                          record.cancelledAt ||
                            record.bookedDate ||
                            record.soldDate ||
                            record.installationDate ||
                            record.qualSatDate ||
                            record.createdAt,
                        )}
                      </td>
                    </tr>
                  );
                })}
              </div>

              {/* Desktop table */}
              <div className="hidden md:block overflow-x-auto">
                {isCancelledReport ? (
                  /* Cancelled appointments: focused columns only */
                  <table className="w-full text-sm">
                    <thead
                      className={`sticky top-0 ${isDarkMode ? "bg-gray-900/60" : "bg-gray-50"}`}
                    >
                      <tr
                        className={`border-b ${isDarkMode ? "border-gray-800" : "border-gray-200"}`}
                      >
                        {[
                          "Opportunity",
                          "Account",
                          "Telemarketer",
                          "Cancellation Reason",
                          "Business Time",
                          "Booked Date",
                          "Cancelled Date",
                        ].map((heading) => (
                          <th
                            key={heading}
                            className={`text-left py-3 px-4 font-medium ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                          >
                            {heading}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody
                      className={`divide-y ${isDarkMode ? "divide-gray-800" : "divide-gray-200"}`}
                    >
                      {detailRecords.map((record, idx) => (
                        <tr
                          key={idx}
                          className={`transition-colors ${isDarkMode ? "hover:bg-gray-800/30" : "hover:bg-gray-50"}`}
                        >
                          <td className="py-3 px-4">
                            <a
                              href={`/opportunities/${record.id}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="font-medium text-blue-400 hover:text-blue-300 hover:underline transition-colors"
                            >
                              {record.name || "Untitled"}
                            </a>
                          </td>
                          <td
                            className={`py-3 px-4 ${isDarkMode ? "text-gray-300" : "text-gray-600"}`}
                          >
                            {record.account?.name ||
                              record["account.name"] ||
                              "-"}
                          </td>
                          <td
                            className={`py-3 px-4 ${isDarkMode ? "text-gray-300" : "text-gray-600"}`}
                          >
                            {record.telesalesPerson || "-"}
                          </td>
                          <td
                            className={`py-3 px-4 max-w-[260px] ${isDarkMode ? "text-gray-300" : "text-gray-600"}`}
                          >
                            {record.cancellationReason ? (
                              <span
                                className="block truncate text-xs italic"
                                title={record.cancellationReason}
                              >
                                {record.cancellationReason}
                              </span>
                            ) : (
                              <span
                                className={`text-xs ${isDarkMode ? "text-gray-600" : "text-gray-400"}`}
                              >
                                No reason given
                              </span>
                            )}
                          </td>
                          <td className="py-3 px-4 whitespace-nowrap">
                            {record.cancelledAt && record.bookedDate ? (
                              <span className="text-orange-400 font-medium text-sm">
                                {(() => {
                                  const ms =
                                    new Date(record.cancelledAt).getTime() -
                                    new Date(record.bookedDate).getTime();
                                  if (isNaN(ms) || ms < 0) return "-";
                                  const totalMins = Math.floor(ms / 60000);
                                  const days = Math.floor(totalMins / 1440);
                                  const hours = Math.floor(
                                    (totalMins % 1440) / 60,
                                  );
                                  const mins = totalMins % 60;
                                  if (days > 0) return `${days}d ${hours}h`;
                                  if (hours > 0) return `${hours}h ${mins}m`;
                                  return `${mins}m`;
                                })()}
                              </span>
                            ) : (
                              <span
                                className={`text-xs ${isDarkMode ? "text-gray-600" : "text-gray-400"}`}
                              >
                                —
                              </span>
                            )}
                          </td>
                          <td
                            className={`py-3 px-4 whitespace-nowrap text-sm ${isDarkMode ? "text-gray-300" : "text-gray-600"}`}
                          >
                            {formatDate(record.bookedDate)}
                          </td>
                          <td
                            className={`py-3 px-4 whitespace-nowrap text-sm ${isDarkMode ? "text-gray-300" : "text-gray-600"}`}
                          >
                            {formatDate(record.cancelledAt)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ) : (
                  /* Standard table for all other reports */
                  <table className="w-full text-sm">
                    <thead
                      className={`sticky top-0 ${isDarkMode ? "bg-gray-900/60" : "bg-gray-50"}`}
                    >
                      <tr
                        className={`border-b ${isDarkMode ? "border-gray-800" : "border-gray-200"}`}
                      >
                        {[
                          "Opportunity",
                          "Account",
                          "Sales Rep",
                          "Telemarketer",
                          "Sales Status",
                          "Installation Status",
                          "Amount",
                          "Units",
                          "Date",
                        ].map((heading, i) => (
                          <th
                            key={heading}
                            className={`${i >= 6 ? "text-right" : "text-left"} py-3 px-4 font-medium ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                          >
                            {heading}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody
                      className={`divide-y ${isDarkMode ? "divide-gray-800" : "divide-gray-200"}`}
                    >
                      {detailRecords.map((record, idx) => {
                        const installStatus =
                          getInstallStatusBadgeClass(record);
                        return (
                          <tr
                            key={idx}
                            className={`transition-colors ${isDarkMode ? "hover:bg-gray-800/30" : "hover:bg-gray-50"}`}
                          >
                            <td className="py-3 px-4">
                              <a
                                href={`/opportunities/${record.id}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="font-medium text-blue-400 hover:text-blue-300 hover:underline transition-colors"
                              >
                                {record.name || "Untitled"}
                              </a>
                            </td>
                            <td
                              className={`py-3 px-4 ${isDarkMode ? "text-gray-300" : "text-gray-600"}`}
                            >
                              {record.account?.name ||
                                record["account.name"] ||
                                "-"}
                            </td>
                            <td
                              className={`py-3 px-4 ${isDarkMode ? "text-gray-300" : "text-gray-600"}`}
                            >
                              {record.assignedUser?.name ||
                                record["assignedUser.name"] ||
                                "-"}
                            </td>
                            <td
                              className={`py-3 px-4 ${isDarkMode ? "text-gray-300" : "text-gray-600"}`}
                            >
                              {record.telesalesPerson || "-"}
                            </td>
                            <td className="py-3 px-4">
                              <span
                                className={`px-2 py-1 rounded border whitespace-nowrap ${getSalesStatusBadgeClass(record.salesStatus)}`}
                              >
                                {formatSalesStatus(record.salesStatus)}
                              </span>
                            </td>
                            <td className="py-3 px-4">
                              {installStatus ? (
                                <span
                                  className={`px-2 py-1 rounded border whitespace-nowrap ${installStatus.class}`}
                                >
                                  {installStatus.text}
                                </span>
                              ) : (
                                <span
                                  className={`text-xs ${isDarkMode ? "text-gray-500" : "text-gray-400"}`}
                                >
                                  -
                                </span>
                              )}
                            </td>
                            <td
                              className={`py-3 px-4 text-right font-medium ${isDarkMode ? "text-white" : "text-gray-900"}`}
                            >
                              {record.amount
                                ? formatCurrency(record.amount)
                                : "-"}
                            </td>
                            <td
                              className={`py-3 px-4 text-right ${isDarkMode ? "text-gray-300" : "text-gray-600"}`}
                            >
                              {record.unitsSold || "-"}
                            </td>
                            <td
                              className={`py-3 px-4 ${isDarkMode ? "text-gray-300" : "text-gray-600"}`}
                            >
                              {formatDate(
                                record.bookedDate ||
                                  record.soldDate ||
                                  record.installationDate ||
                                  record.qualSatDate ||
                                  record.createdAt,
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                )}
              </div>
            </>
          )}
        </div>

        {/* Footer */}
        <div
          className={`p-4 border-t flex justify-between items-center ${isDarkMode ? "border-gray-800" : "border-gray-200"}`}
        >
          <span
            className={`text-sm ${isDarkMode ? "text-gray-500" : "text-gray-400"}`}
          >
            {detailRecords.length} record{detailRecords.length !== 1 ? "s" : ""}
          </span>
          <button
            onClick={onClose}
            className={`px-6 py-2 rounded-lg transition-colors ${isDarkMode ? "bg-gray-800 hover:bg-gray-700 text-white" : "bg-gray-200 hover:bg-gray-300 text-gray-900"}`}
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default SalesDashboard;
